import { InputFrame } from '../../sim/input';

/** What the Gamepad API gives, reduced to plain numbers (standard mapping). */
export interface PadSnapshot { axes: readonly number[]; buttons: readonly number[] }

export type PadAction = 'up' | 'down' | 'left' | 'right' | 'accept' | 'back' | 'pause' | 'stand' | 'reset' | 'camera';

/** `beginner` (the default scheme): LT (or B) is the one brake the assist splits, the left stick's Y leans, stance and foot are automatic. */
export interface PadState { held: Set<PadAction>; standing: boolean; beginner: boolean }
export const newPadState = (): PadState => ({ held: new Set(), standing: false, beginner: false });

/** Standard mapping button indices. */
export const PAD = { A: 0, B: 1, X: 2, Y: 3, LT: 6, RT: 7, SELECT: 8, START: 9, L3: 10, R3: 11, UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const;

const dead = (v: number, d = 0.15) => (Math.abs(v) < d ? 0 : (v - Math.sign(v) * d) / (1 - d));

/**
 * One poll: the riding input. Default scheme: RT gas, LT (or B) brake, the left stick steers (X) and leans (Y: back =
 * lean back, the trick key) - the right stick's Y leans too. Pro controls: and the actions that went down since the last poll (edges, so holding A or the
 * stick does not repeat). Riding: RT throttle, LT front brake, B rear brake, left stick steer, right stick Y body,
 * A held flips the rider's own sit / stand choice like Space, X holds the foot out, Y resets like R, View (Back) or a right-stick click cycles
 * the camera like C; in menus the same presses are
 * `accept` / `back`.
 */
export function readPad(s: PadSnapshot, st: PadState): { input: InputFrame; pressed: PadAction[] } {
  const btn = (i: number) => s.buttons[i] ?? 0;
  const ax = (i: number) => s.axes[i] ?? 0;
  const lx = ax(0), ly = ax(1), ry = ax(3);
  const now = new Set<PadAction>();
  if (btn(PAD.UP) > 0.5 || ly < -0.5) now.add('up');
  if (btn(PAD.DOWN) > 0.5 || ly > 0.5) now.add('down');
  if (btn(PAD.LEFT) > 0.5 || lx < -0.5) now.add('left');
  if (btn(PAD.RIGHT) > 0.5 || lx > 0.5) now.add('right');
  if (btn(PAD.A) > 0.5) { now.add('accept'); now.add('stand'); }
  if (btn(PAD.B) > 0.5) now.add('back');
  if (btn(PAD.START) > 0.5) now.add('pause');
  if (btn(PAD.Y) > 0.5) now.add('reset');
  if (btn(PAD.SELECT) > 0.5 || btn(PAD.R3) > 0.5) now.add('camera');
  const pressed = [...now].filter((a) => !st.held.has(a));
  st.held = now;
  st.standing = now.has('stand'); // held: flips the rider's own sit / stand choice (MX physics row 1)
  // Right stick up leans forward (+1, like E); left stick left steers left (+1, like A).
  return {
    input: st.beginner
      ? { throttle: btn(PAD.RT), brake: Math.max(btn(PAD.LT), btn(PAD.B)), steer: -dead(lx) || 0, body: padLean(ly, ry), standing: false, autoPosture: true, autoFoot: true, assist: true }
      : {
        throttle: btn(PAD.RT), brake: 0, frontBrake: btn(PAD.LT), rearBrake: btn(PAD.B), steer: -dead(lx) || 0, body: -dead(ry) || 0,
        standing: st.standing, autoPosture: true, foot: btn(PAD.X) > 0.5,
      },
    pressed,
  };
}

/** Default scheme lean: the left stick's Y (up = forward, +1), or the right stick's if it is pushed further. */
export function padLean(ly: number, ry: number): number {
  const l = -dead(ly, 0.3) || 0, r = -dead(ry) || 0;
  return Math.abs(r) > Math.abs(l) ? r : l;
}

/** True when the pad is being used for riding this frame (so it overrides the keyboard). */
export const padRiding = (i: InputFrame) => i.throttle > 0.02 || i.brake > 0.02 || (i.frontBrake ?? 0) > 0.02 || (i.rearBrake ?? 0) > 0.02
  || i.steer !== 0 || i.body !== 0 || !!i.foot;
